import React, { useCallback, useMemo, useState } from 'react';
import { Card, Button, Space, Typography, Tooltip, message } from 'antd';
import {
  ReloadOutlined,
  HeartOutlined,
  HeartFilled,
  CopyOutlined,
} from '@ant-design/icons';
import quotesData from '../data/quotes.json';
import { Quote } from '../types';

const { Paragraph } = Typography;

const quotes: Quote[] = quotesData as Quote[];

function pickRandomIndex(excludeIndex?: number): number {
  if (quotes.length <= 1) return 0;
  let index = Math.floor(Math.random() * quotes.length);
  while (index === excludeIndex) {
    index = Math.floor(Math.random() * quotes.length);
  }
  return index;
}

const QuoteCard: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState<number>(() =>
    pickRandomIndex()
  );
  const [likedIndexes, setLikedIndexes] = useState<Set<number>>(new Set());
  const [spinning, setSpinning] = useState(false);

  const currentQuote = useMemo(() => quotes[currentIndex], [currentIndex]);
  const isLiked = likedIndexes.has(currentIndex);

  const handleNewQuote = useCallback(() => {
    setSpinning(true);
    setCurrentIndex((prev) => pickRandomIndex(prev));
    window.setTimeout(() => setSpinning(false), 400);
  }, []);

  const handleToggleLike = useCallback(() => {
    setLikedIndexes((prev) => {
      const next = new Set(prev);
      if (next.has(currentIndex)) {
        next.delete(currentIndex);
      } else {
        next.add(currentIndex);
      }
      return next;
    });
  }, [currentIndex]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(currentQuote);
      message.success('Đã sao chép trích dẫn!');
    } catch {
      message.error('Không thể sao chép. Vui lòng thử lại.');
    }
  }, [currentQuote]);

  return (
    <Card
      className="quote-card"
      title="Trích dẫn truyền cảm hứng"
      extra={
        <Tooltip title="Trích dẫn mới">
          <Button
            type="text"
            shape="circle"
            icon={<ReloadOutlined spin={spinning} />}
            onClick={handleNewQuote}
          />
        </Tooltip>
      }
    >
      <Paragraph className="quote-text">“{currentQuote}”</Paragraph>
      <Space>
        <Tooltip title={isLiked ? 'Bỏ thích' : 'Thích trích dẫn này'}>
          <Button
            shape="circle"
            icon={isLiked ? <HeartFilled /> : <HeartOutlined />}
            className={isLiked ? 'liked' : undefined}
            onClick={handleToggleLike}
          />
        </Tooltip>
        <Tooltip title="Sao chép">
          <Button
            shape="circle"
            icon={<CopyOutlined />}
            onClick={handleCopy}
          />
        </Tooltip>
      </Space>
    </Card>
  );
};

export default QuoteCard;
